import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { PaperPlaneTilt, Bell, Users, ClockCounterClockwise, ArrowClockwise, Trash } from '@phosphor-icons/react';
import { pushApi } from '../lib/api';

const ALL_ROLES = [
  { key: 'admin', label: 'Administrateurs' },
  { key: 'director_studies', label: 'Directeurs des études' },
  { key: 'comptable', label: 'Comptables' },
  { key: 'rh', label: 'RH' },
  { key: 'secretary', label: 'Secrétariat' },
  { key: 'user', label: 'Utilisateurs' },
  { key: 'teacher_user', label: 'Enseignants' },
  { key: 'parent', label: 'Parents' },
  { key: 'cantine_staff', label: 'Personnel cantine' },
  { key: 'transport_staff', label: 'Personnel transport' },
];

const QUICK_TEMPLATES = [
  { label: '⚠️ Fermeture', title: '⚠️ Fermeture exceptionnelle', body: 'L\'école sera fermée demain. Consultez le communiqué pour les détails.', roles: ['admin', 'director_studies', 'teacher_user', 'parent'] },
  { label: '🎉 Événement', title: '🎉 Événement à venir', body: 'Rejoignez-nous pour la kermesse samedi à 15h dans la cour !', roles: ['teacher_user', 'parent'] },
  { label: '📝 Réunion', title: '📝 Réunion pédagogique', body: 'Réunion enseignants demain 17h en salle des professeurs.', roles: ['teacher_user'] },
  { label: '💰 Rappel paiement', title: '💰 Rappel scolarité', body: 'Merci de régler la 2ème tranche avant le 30 du mois.', roles: ['parent'] },
];

export default function AdminPushBroadcast() {
  const [stats, setStats] = useState(null);
  const [roles, setRoles] = useState(['admin', 'director_studies']);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/dashboard');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [filters, setFilters] = useState({ since: '', until: '', role: '' });

  const refreshAll = () => {
    pushApi.adminStats().then(({ data }) => setStats(data)).catch(() => {});
    pushApi.adminHistory({ limit: 50, ...filters })
      .then(({ data }) => setHistory(data.items || []))
      .catch(() => {});
  };

  useEffect(() => {
    refreshAll();
  }, [filters.since, filters.until, filters.role]);

  const toggleRole = (r) => {
    setRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  };

  const applyTemplate = (tpl) => {
    setTitle(tpl.title);
    setBody(tpl.body);
    setRoles(tpl.roles);
  };

  const remainingTitle = 80 - title.length;
  const remainingBody = 180 - body.length;
  const canSend = roles.length > 0 && title.trim() && body.trim() && !sending;

  const audience = roles.reduce((sum, r) => sum + (stats?.per_role?.[r]?.subscriptions || 0), 0);
  const audienceUsers = roles.reduce((sum, r) => sum + (stats?.per_role?.[r]?.users || 0), 0);

  const handleSend = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await pushApi.adminBroadcast({ roles, title: title.trim(), body: body.trim(), url });
      setLastResult(data);
      toast.success(`Push envoyé — ${data.sent} livrée(s), ${data.failed} échec(s), ${data.users || 0} utilisateur(s) touché(s)`);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec envoi');
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (logId) => {
    setResendingId(logId);
    try {
      const { data } = await pushApi.adminResend(logId);
      toast.success(`Renvoyé — ${data.sent} livrée(s), ${data.failed} échec(s)`);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec renvoi');
    } finally {
      setResendingId(null);
    }
  };

  const handleDelete = async (logId) => {
    try {
      await pushApi.adminDeleteLog(logId);
      toast.info('Entrée supprimée de l\'historique');
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec suppression');
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-push-broadcast">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-[#0F172A] flex items-center gap-2">
          <Bell size={26} weight="duotone" className="text-indigo-600" />
          Broadcast Push
        </h1>
        <p className="text-sm text-[#64748B] mt-1">
          Envoyez une notification push instantanée à un ou plusieurs rôles. Idéal pour les communications urgentes.
        </p>
      </div>

      {/* Stats overview */}
      {stats && (
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="push-stats-card">
          <CardContent className="py-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <p className="text-xs text-[#64748B]">Souscriptions actives</p>
              <p className="font-outfit text-xl font-bold text-emerald-600" data-testid="stats-active">{stats.active}</p>
            </div>
            <div>
              <p className="text-xs text-[#64748B]">Total (incl. inactives)</p>
              <p className="font-outfit text-xl font-bold text-[#0F172A]">{stats.total}</p>
            </div>
            <div>
              <p className="text-xs text-[#64748B]">Rôles couverts</p>
              <p className="font-outfit text-xl font-bold text-[#0F172A]">{Object.keys(stats.per_role || {}).length}</p>
            </div>
            <div>
              <p className="text-xs text-[#64748B]">VAPID</p>
              <p className={`font-outfit text-xl font-bold ${stats.vapid_configured ? 'text-emerald-600' : 'text-red-600'}`}>
                {stats.vapid_configured ? 'OK' : 'KO'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick templates */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-sm text-[#475569]">Modèles rapides</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 flex flex-wrap gap-2">
          {QUICK_TEMPLATES.map((tpl) => (
            <Button
              key={tpl.label}
              variant="outline"
              size="sm"
              onClick={() => applyTemplate(tpl)}
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              data-testid={`push-template-${tpl.label}`}
            >
              {tpl.label}
            </Button>
          ))}
        </CardContent>
      </Card>

      {/* Composer */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-base">Composer</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {/* Roles multi-select */}
          <div>
            <Label className="text-xs font-semibold text-[#475569] mb-2 flex items-center gap-1">
              <Users size={14} /> Destinataires ({roles.length} rôle(s))
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {ALL_ROLES.map((r) => {
                const active = roles.includes(r.key);
                const count = stats?.per_role?.[r.key]?.subscriptions || 0;
                return (
                  <button
                    key={r.key}
                    onClick={() => toggleRole(r.key)}
                    data-testid={`role-toggle-${r.key}`}
                    className={`px-3 py-2 rounded-lg border text-left text-sm transition-colors ${
                      active
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-[#0F172A] border-[#E2E8F0] hover:bg-indigo-50'
                    }`}
                  >
                    <div className="font-medium">{r.label}</div>
                    <div className={`text-xs mt-0.5 ${active ? 'text-white/80' : 'text-[#64748B]'}`}>
                      {count} abonné(s)
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-indigo-600 mt-2">
              Audience estimée : <span className="font-semibold" data-testid="audience-estimate">{audience}</span> souscriptions
              ({audienceUsers} utilisateur(s))
            </p>
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="push-title" className="text-xs font-semibold text-[#475569]">
              Titre <span className="text-[#94A3B8]">({remainingTitle} caractères restants)</span>
            </Label>
            <Input
              id="push-title"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 80))}
              placeholder="⚠️ Fermeture exceptionnelle demain"
              className="border-[#E2E8F0] mt-1"
              data-testid="push-title-input"
            />
          </div>

          {/* Body */}
          <div>
            <Label htmlFor="push-body" className="text-xs font-semibold text-[#475569]">
              Message <span className="text-[#94A3B8]">({remainingBody} caractères restants)</span>
            </Label>
            <Textarea
              id="push-body"
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 180))}
              placeholder="Merci de consulter le communiqué détaillé sur le dashboard."
              rows={3}
              className="border-[#E2E8F0] mt-1"
              data-testid="push-body-input"
            />
          </div>

          {/* URL */}
          <div>
            <Label htmlFor="push-url" className="text-xs font-semibold text-[#475569]">
              Lien profond (au clic)
            </Label>
            <Input
              id="push-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/dashboard"
              className="border-[#E2E8F0] mt-1 font-mono text-sm"
              data-testid="push-url-input"
            />
            <p className="text-xs text-[#94A3B8] mt-1">
              Ex : /dashboard, /parent-dashboard, /admin/security, /m/teacher/appointments
            </p>
          </div>

          {/* Preview */}
          {(title || body) && (
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="push-preview">
              <p className="text-xs text-[#94A3B8] mb-2">Aperçu de la notification :</p>
              <div className="bg-white rounded-md p-3 border border-slate-300 shadow-sm">
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded bg-indigo-600 flex items-center justify-center shrink-0">
                    <Bell size={16} className="text-white" weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#0F172A]">{title || 'Titre de la notification'}</p>
                    <p className="text-sm text-[#475569]">{body || 'Corps du message…'}</p>
                    <p className="text-[10px] text-[#94A3B8] mt-1">Gestion École · maintenant</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Button
            onClick={handleSend}
            disabled={!canSend}
            className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto"
            data-testid="push-send-btn"
          >
            <PaperPlaneTilt size={16} weight="duotone" className="mr-2" />
            {sending ? 'Envoi…' : `Envoyer à ${audience} appareil(s)`}
          </Button>

          {lastResult && (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm" data-testid="push-last-result">
              <p className="font-semibold text-emerald-800">Dernier envoi</p>
              <p className="text-emerald-700">
                Livrées : <b>{lastResult.sent}</b> · Échouées : <b>{lastResult.failed}</b>
                {' '}· Désactivées : <b>{lastResult.deactivated}</b>
                {' '}· Utilisateurs touchés : <b>{lastResult.users || 0}</b>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* History */}
      <Card className="border border-[#E2E8F0] shadow-none" data-testid="push-history-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <ClockCounterClockwise size={18} weight="duotone" className="text-slate-600" />
            Historique des broadcasts
            <span className="ml-auto text-xs text-[#94A3B8] font-manrope font-normal">
              {history.length} entrée(s)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Filters row */}
          <div className="flex flex-wrap items-end gap-3 mb-3 pb-3 border-b border-[#F1F5F9]">
            <div>
              <Label className="text-[10px] text-[#64748B] uppercase">Depuis</Label>
              <Input
                type="date"
                value={filters.since}
                onChange={(e) => setFilters({ ...filters, since: e.target.value })}
                className="h-8 text-xs w-36"
                data-testid="history-filter-since"
              />
            </div>
            <div>
              <Label className="text-[10px] text-[#64748B] uppercase">Jusqu&apos;au</Label>
              <Input
                type="date"
                value={filters.until}
                onChange={(e) => setFilters({ ...filters, until: e.target.value })}
                className="h-8 text-xs w-36"
                data-testid="history-filter-until"
              />
            </div>
            <div>
              <Label className="text-[10px] text-[#64748B] uppercase">Rôle</Label>
              <select
                value={filters.role}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                className="h-8 text-xs px-2 border border-[#E2E8F0] rounded-md bg-white"
                data-testid="history-filter-role"
              >
                <option value="">Tous les rôles</option>
                {ALL_ROLES.map(r => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </div>
            {(filters.since || filters.until || filters.role) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setFilters({ since: '', until: '', role: '' })}
                className="text-red-600 hover:bg-red-50 h-8"
                data-testid="history-filter-reset"
              >
                Réinitialiser
              </Button>
            )}
          </div>

          {history.length === 0 ? (
            <div className="text-center py-6 text-sm text-[#94A3B8]">
              <ClockCounterClockwise size={28} className="mx-auto mb-2 opacity-50" />
              <p>
                {(filters.since || filters.until || filters.role)
                  ? 'Aucun broadcast ne correspond aux filtres.'
                  : <>Aucun broadcast pour l&apos;instant. Envoyez-en un ci-dessus.</>}
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {history.map((h) => (
                <div
                  key={h.id}
                  className="p-3 rounded-lg border border-[#E2E8F0] hover:bg-slate-50/50"
                  data-testid={`push-history-item-${h.id}`}
                >
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-outfit font-semibold text-sm text-[#0F172A]">{h.title}</p>
                        {h.resend_of && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                            <ArrowClockwise size={10} /> Renvoi
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#475569] line-clamp-2 mt-0.5">{h.body}</p>
                      <div className="flex items-center gap-2 flex-wrap mt-1.5">
                        {(h.roles || []).map((r) => (
                          <span
                            key={r}
                            className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded"
                          >
                            {r}
                          </span>
                        ))}
                      </div>
                      <p className="text-[10px] text-[#94A3B8] mt-1">
                        {new Date(h.created_at).toLocaleString('fr-FR', {
                          hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: '2-digit',
                        })}
                        {' · '}par {h.by_user_name}
                        {' · '}→ <span className="font-mono">{h.url}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span
                        className={`px-2 py-0.5 rounded ${
                          h.sent > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                        }`}
                        data-testid={`push-history-metrics-${h.id}`}
                      >
                        ✓ {h.sent} · ✗ {h.failed}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={resendingId === h.id}
                        onClick={() => handleResend(h.id)}
                        className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        data-testid={`push-resend-${h.id}`}
                      >
                        <ArrowClockwise size={14} className="mr-1" />
                        {resendingId === h.id ? '…' : 'Renvoyer'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(h.id)}
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`push-delete-${h.id}`}
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
